import React, { FC, ReactElement, useContext, useMemo } from 'react'
import { LicenseManagement as LicenseManagementLanguage } from '../../../../../i18n/index'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { useFormContext, UseFormMethods } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { TelSysForm, TelSysLicenceManagement } from '../../../TelSysConfiguration/Types/Types'
import { LanguageContext } from '../../Contexts'
import { Table, TableData, TableRow } from '../Table/Table'
import { createHeaderRow } from '../Table/TableUtils'
import { AlignPositions } from '../Table/types'
import createRow from './CreateRow'
import { LicenceResponseMapped } from '../../../../NonReact/Types/Types'
import { RowData, TableEntry } from './types'
import { COMMS_ENABLED_FIELD_NAME } from '../../../TelSysConfiguration/Constants/FieldNames'
import { CMEFormHookData } from '../../../CMEAddModify/Types'
import { TelSysTypes } from '../../../../NonReact/constants/TelephonyServerTypes'

/**
 * Create table data entries.
 *
 * @param methods - Form methods.
 * @param language - Language handler.
 * @param title - Title for the entry.
 * @param headings - Headings for the entry.
 * @param rows - Rows for the entry.
 */
const tableDataCreator = (
    methods: UseFormMethods<CMEFormHookData> | UseFormMethods<TelSysForm>,
    language: CLanguageHandler,
    { title, headings, rows }: TableEntry
): {
    title: string
    tableData: TableData
} => ({
    title,
    tableData: {
        headings: createHeaderRow(AlignPositions.LEFT, ...headings),
        rows: rows
            .filter((rowItem: RowData): boolean => (IsVarEmpty(rowItem?.validation) ? true : rowItem.validation))
            .map(
                ({ name, title: rowTitle, rowClassName, limit, disabled }: RowData): TableRow =>
                    createRow(methods, language, name, rowClassName, limit, disabled, ...rowTitle)
            )
    }
})

/**
 * The function calculates the license limit by adding the license number to the original number, if
 * provided, or returns the license number if the original number is empty.
 * @param license - The `license` parameter is a nullable number, which means it can either be a number
 * or null.
 * @param [original] - The `original` parameter is an optional parameter of type `Nullable<number>`. It
 * represents the original license limit that is being calculated.
 * @returns The function `getCalculatedLicenseLimit` returns a nullable number.
 */
const getCalculatedLicenseLimit = (license: Nullable<number>, original?: Nullable<number>): Nullable<number> => {
    if (IsVarEmpty(license)) return null
    if (!IsVarEmpty(original)) return license + original
    return Number(license)
}

type Props = {
    formNames: {
        essentials: string
        professional: string
        enterprise: string
        callCentreAgent: string
        callCentreSupervisor: string
        cxIntegration: string
    }
    limitations?: Nullable<LicenceResponseMapped>
    originalLicenseManagementValues?: Nullable<TelSysLicenceManagement<Nullable<number>>>
    methods: UseFormMethods<CMEFormHookData> | UseFormMethods<TelSysForm>
    isCxiAvailable: boolean
    isViewOnly?: boolean
}

const TELSYS_TYPE: Definable<string> = TelSysTypes.MS_TEAMS

export const LicenceManagement: FC<Props> = ({
    formNames: { essentials, enterprise, callCentreAgent, callCentreSupervisor, cxIntegration },
    limitations,
    originalLicenseManagementValues,
    methods,
    isCxiAvailable,
    isViewOnly
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(LicenseManagementLanguage.NAME)

    const { watch }: UseFormMethods = useFormContext()

    const isCommsEnabled: boolean = watch(COMMS_ENABLED_FIELD_NAME)

    const licenceManagementTable: TableEntry = useMemo(
        (): TableEntry => ({
            title: language.get(LicenseManagementLanguage.LicenseManagement_Title),
            headings: [
                language.get(LicenseManagementLanguage.LicenseManagement_Heading_License),
                language.get(LicenseManagementLanguage.LicenseManagement_Heading_Quantity)
            ],
            rows: [
                {
                    name: essentials,
                    title: [
                        language.getWithTelSysCheck(LicenseManagementLanguage.LicenseManagement_Essentials, TELSYS_TYPE)
                    ],
                    rowClassName: 'lm-essentials',
                    limit: getCalculatedLicenseLimit(
                        limitations?.AvailableLicences?.Essentials,
                        isCommsEnabled ? originalLicenseManagementValues?.essentials : undefined
                    ),
                    disabled: isViewOnly
                },
                {
                    name: enterprise,
                    title: [
                        language.getWithTelSysCheck(LicenseManagementLanguage.LicenseManagement_Enterprise, TELSYS_TYPE)
                    ],
                    rowClassName: 'lm-enterprise',
                    limit: getCalculatedLicenseLimit(
                        limitations?.AvailableLicences?.Enterprise,
                        isCommsEnabled ? originalLicenseManagementValues?.enterprise : undefined
                    ),
                    disabled: isViewOnly
                },
                {
                    name: callCentreAgent,
                    title: [
                        language.getWithTelSysCheck(
                            LicenseManagementLanguage.LicenseManagement_CallCentreAgent,
                            TELSYS_TYPE
                        )
                    ],
                    rowClassName: 'lm-enterprise',
                    limit: getCalculatedLicenseLimit(
                        limitations?.AvailableLicences?.CallCentreAgent,
                        isCommsEnabled ? originalLicenseManagementValues?.callCentreAgent : undefined
                    ),
                    disabled: isViewOnly
                },
                {
                    name: callCentreSupervisor,
                    title: [
                        language.getWithTelSysCheck(
                            LicenseManagementLanguage.LicenseManagement_CallCentreSupervisor,
                            TELSYS_TYPE
                        )
                    ],
                    rowClassName: 'lm-enterprise',
                    limit: getCalculatedLicenseLimit(
                        limitations?.AvailableLicences?.CallCentreSupervisor,
                        isCommsEnabled ? originalLicenseManagementValues?.callCentreSupervisor : undefined
                    ),
                    disabled: isViewOnly
                },
                ...(isCxiAvailable
                    ? [
                          {
                              name: cxIntegration,
                              title: [language.get(LicenseManagementLanguage.LicenseManagement_CXIntegration)],
                              rowClassName: 'lm-cxintegration',
                              limit: getCalculatedLicenseLimit(
                                  limitations?.AvailableLicences?.CXIntegration,
                                  isCommsEnabled ? originalLicenseManagementValues?.cxIntegration : undefined
                              ),
                              disabled: isViewOnly
                          }
                      ]
                    : [])
            ]
        }),
        [JSON.stringify(limitations), JSON.stringify(originalLicenseManagementValues), isCxiAvailable, isViewOnly]
    )

    return (
        <div className='lm-table-container'>
            {licenceManagementTable ? (
                <Table {...tableDataCreator(methods, language, licenceManagementTable)} />
            ) : (
                <></>
            )}
        </div>
    )
}
